import React, { useEffect, useState } from "react";
import { Loader2, Save, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/adminApi";
import ImageUploadField from "../../components/admin/ImageUploadField";

const FIELDS = [
    { key: "phone_display",   label: "Phone (display format)", icon: Phone,         placeholder: "+91 89997 22515" },
    { key: "phone_tel",       label: "Phone (tel: link)",      icon: Phone,         placeholder: "+918999722515", helpText: "Used for tel: links — digits only with country code." },
    { key: "whatsapp_number", label: "WhatsApp Number",        icon: MessageCircle, placeholder: "918999722515", helpText: "Digits only, country code first. Used in https://wa.me/{number}." },
    { key: "email",           label: "Email",                  icon: Mail,          placeholder: "hello@rajgraphics.in" },
    { key: "address",         label: "Studio Address",         icon: MapPin,        placeholder: "Nagpur, Maharashtra · India" },
];

export const AdminContactPage = () => {
    const [data, setData] = useState({
        phone_display: "",
        phone_tel: "",
        whatsapp_number: "",
        email: "",
        address: "",
        logo_url: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await adminApi.get("/admin/contact");
                if (mounted) setData({ logo_url: "", ...res.data });
            } catch (err) {
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : "Failed to load");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await adminApi.put("/admin/contact", data);
            setData({ logo_url: "", ...res.data });
            toast.success("Contact info updated");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="manager-contact" className="p-6 md:p-10 max-w-3xl mx-auto">
            <div className="mb-8">
                <h1 className="font-poppins font-semibold text-white text-2xl md:text-3xl tracking-tight">
                    Contact &amp; Branding
                </h1>
                <p className="mt-2 font-inter text-sm text-zinc-400">
                    Update your studio's contact details and logo. Changes appear across the public site.
                </p>
            </div>

            {loading ? (
                <div className="flex items-center gap-3 text-zinc-500">
                    <Loader2 size={16} className="animate-spin" /> Loading…
                </div>
            ) : (
                <form
                    onSubmit={onSave}
                    data-testid="contact-form"
                    className="space-y-8"
                >
                    {/* Logo */}
                    <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-7 md:p-9">
                        <div className="font-inter text-xs uppercase tracking-[0.2em] text-gold mb-4">
                            Studio Logo
                        </div>
                        <p className="font-inter text-sm text-zinc-400 mb-5">
                            Upload your final brand logo (PNG with transparent background works best).
                            When uploaded, it replaces the default "R" wordmark in the header and footer of every page.
                            Leave empty to keep the default wordmark.
                        </p>
                        <ImageUploadField
                            value={data.logo_url || ""}
                            onChange={(v) => setField("logo_url", v)}
                            testId="contact-field-logo"
                            helpText="Recommended: PNG with transparent background, ~200×60 px, max 3 MB."
                        />
                    </section>

                    {/* Contact details */}
                    <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-7 md:p-9 space-y-6">
                        <div className="font-inter text-xs uppercase tracking-[0.2em] text-gold">
                            Contact Details
                        </div>
                        {FIELDS.map((f) => {
                            const Icon = f.icon;
                            return (
                                <div key={f.key}>
                                    <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                                        <Icon size={12} className="text-gold" />
                                        {f.label}
                                    </label>
                                    <input
                                        type="text"
                                        value={data[f.key] || ""}
                                        onChange={(e) => setField(f.key, e.target.value)}
                                        placeholder={f.placeholder}
                                        data-testid={`contact-field-${f.key}`}
                                        className="bg-black/40 border border-white/10 focus:border-gold rounded-lg px-4 py-2.5 text-white outline-none w-full transition-colors font-inter text-sm"
                                    />
                                    {f.helpText && (
                                        <p className="mt-1.5 font-inter text-xs text-zinc-500">{f.helpText}</p>
                                    )}
                                </div>
                            );
                        })}
                    </section>

                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="contact-save-btn"
                        className="inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] disabled:opacity-60 text-black font-medium font-inter px-7 py-3 rounded-full transition-colors"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={15} />}
                        Save changes
                    </button>
                </form>
            )}
        </div>
    );
};

export default AdminContactPage;
